import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, XCircle, Shield, GraduationCap, Globe, Wallet, FileText, ChevronRight, Download, X, Mail } from 'lucide-react';
import { buildEligibilityPdf } from './utils/eligibilityPdf';
import { usePageMeta } from '@/hooks/useSEO';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 'qualification', title: 'Education Background', icon: GraduationCap },
  { id: 'academics', title: 'Academic Score', icon: FileText },
  { id: 'english', title: 'English Proficiency', icon: Globe },
  { id: 'funds', title: 'Available Funds', icon: Wallet },
  { id: 'destination', title: 'Preferred Country', icon: Shield },
];

const qualificationOptions = [
  { value: '12th', label: '12th / HSC' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'diploma', label: 'Diploma' },
];

const passingYearOptions = Array.from({ length: 10 }, (_, i) => {
  const y = 2026 - i;
  return { value: String(y), label: String(y) };
});

const gpaOptions = [
  { value: 'below60', label: 'Below 60%', gpa: 'Below 6.0' },
  { value: '60-70', label: '60% - 70%', gpa: '6.0 - 7.0' },
  { value: '70-80', label: '70% - 80%', gpa: '7.0 - 8.0' },
  { value: '80-90', label: '80% - 90%', gpa: '8.0 - 9.0' },
  { value: 'above90', label: 'Above 90%', gpa: '9.0+' },
];

const englishTestOptions = [
  { value: 'none', label: 'Not Taken Yet' },
  { value: 'ielts', label: 'IELTS' },
  { value: 'pte', label: 'PTE Academic' },
  { value: 'toefl', label: 'TOEFL iBT' },
];

const englishScoreOptions = {
  ielts: [
    { value: 'below5.5', label: 'Below 5.5' },
    { value: '5.5-6.0', label: '5.5 - 6.0' },
    { value: '6.0-6.5', label: '6.0 - 6.5' },
    { value: '6.5-7.0', label: '6.5 - 7.0' },
    { value: '7.0+', label: '7.0+' },
  ],
  pte: [
    { value: 'below50', label: 'Below 50' },
    { value: '50-58', label: '50 - 58' },
    { value: '58-65', label: '58 - 65' },
    { value: '65-79', label: '65 - 79' },
    { value: '79+', label: '79+' },
  ],
  toefl: [
    { value: 'below72', label: 'Below 72' },
    { value: '72-86', label: '72 - 86' },
    { value: '86-100', label: '86 - 100' },
    { value: '100+', label: '100+' },
  ],
};

const fundsOptions = [
  { value: 'under10', label: 'Under 10 Lakh INR' },
  { value: '10-20', label: '10 - 20 Lakh INR' },
  { value: '20-35', label: '20 - 35 Lakh INR' },
  { value: 'above35', label: 'Above 35 Lakh INR' },
];

const countryOptions = [
  { value: 'germany', label: 'Germany', flag: 'DE' },
  { value: 'uk', label: 'United Kingdom', flag: 'GB' },
  { value: 'canada', label: 'Canada', flag: 'CA' },
  { value: 'australia', label: 'Australia', flag: 'AU' },
  { value: 'ireland', label: 'Ireland', flag: 'IE' },
];

function getFlagEmoji(code) {
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt()));
}

// ─── Eligibility Logic ───
function evaluateEligibility(data) {
  const { qualification, passingYear, gpa, englishTest, englishScore, funds, destination } = data;
  const results = [];
  const gap = 2026 - Number(passingYear);

  // Academic baseline
  const isPostgrad = qualification === 'bachelors' || qualification === 'masters';
  const academicStrong = ['70-80', '80-90', 'above90'].includes(gpa);
  const academicMid = gpa === '60-70';
  const hasGap = gap > 3;

  // English proficiency mapping (normalize to IELTS equivalent)
  let ieltsEquiv = 0;
  if (englishTest === 'none') ieltsEquiv = 0;
  else if (englishTest === 'ielts') {
    if (englishScore === '7.0+') ieltsEquiv = 7.5;
    else if (englishScore === '6.5-7.0') ieltsEquiv = 6.75;
    else if (englishScore === '6.0-6.5') ieltsEquiv = 6.25;
    else if (englishScore === '5.5-6.0') ieltsEquiv = 5.75;
    else ieltsEquiv = 5;
  } else if (englishTest === 'pte') {
    if (englishScore === '79+') ieltsEquiv = 7.5;
    else if (englishScore === '65-79') ieltsEquiv = 6.75;
    else if (englishScore === '58-65') ieltsEquiv = 6.25;
    else if (englishScore === '50-58') ieltsEquiv = 5.75;
    else ieltsEquiv = 5;
  } else if (englishTest === 'toefl') {
    if (englishScore === '100+') ieltsEquiv = 7.5;
    else if (englishScore === '86-100') ieltsEquiv = 6.75;
    else if (englishScore === '72-86') ieltsEquiv = 6.25;
    else ieltsEquiv = 5;
  }

  const fundsStrong = ['20-35', 'above35'].includes(funds);
  const fundsMid = funds === '10-20';

  // Helper to flatten 2-level ternaries: returns 'pass' / 'caution' / 'fail'
  // based on which condition matches first. Improves readability of country
  // eligibility blocks below (replaces nested `a ? b : c ? d : e` chains).
  const gradeBy = (passCond, cautionCond) => {
    if (passCond) return 'pass';
    if (cautionCond) return 'caution';
    return 'fail';
  };
  const pickNote = (passCond, passNote, cautionCond, cautionNote, failNote) => {
    if (passCond) return passNote;
    if (cautionCond) return cautionNote;
    return failNote;
  };

  // ─── GERMANY ───
  const gResult = { country: 'Germany', flag: 'DE', slug: 'germany', checks: [] };
  gResult.checks.push({
    label: 'APS Certificate Eligibility',
    status: gradeBy(isPostgrad && academicStrong, isPostgrad),
    note: (isPostgrad && academicStrong) ? 'Strong profile for APS' : 'APS requires verified academic transcripts',
  });
  gResult.checks.push({
    label: 'Blocked Account (~\u20AC11,208/yr)',
    status: gradeBy(fundsStrong, fundsMid),
    note: pickNote(fundsStrong, 'Funds sufficient for blocked account', fundsMid, 'May need education loan support', 'Blocked account requires ~10L INR'),
  });
  gResult.checks.push({
    label: 'Language (German/English)',
    status: gradeBy(ieltsEquiv >= 6.0, ieltsEquiv > 0),
    note: ieltsEquiv >= 6.0 ? 'English programs available' : 'Most programs need IELTS 6.0+; German programs need B2',
  });
  gResult.checks.push({
    label: 'Academic Gap',
    status: hasGap ? 'caution' : 'pass',
    note: hasGap ? `${gap}-year gap may need justification` : 'No significant gap',
  });
  gResult.score = gResult.checks.filter(c => c.status === 'pass').length;
  results.push(gResult);

  // ─── UK ───
  const uResult = { country: 'United Kingdom', flag: 'GB', slug: 'uk', checks: [] };
  uResult.checks.push({
    label: 'CAS & Point-Based System',
    status: gradeBy(isPostgrad && academicStrong, isPostgrad),
    note: (isPostgrad && academicStrong) ? 'Strong for Tier 4 student visa' : 'Universities have varying entry requirements',
  });
  uResult.checks.push({
    label: 'Financial Proof (~\u00A312,006/yr + tuition)',
    status: gradeBy(fundsStrong, fundsMid),
    note: pickNote(fundsStrong, 'Sufficient for 28-day maintenance', fundsMid, 'May need education loan', 'UK requires higher fund proof'),
  });
  uResult.checks.push({
    label: 'IELTS/SELT (min 6.0 overall)',
    status: gradeBy(ieltsEquiv >= 6.0, ieltsEquiv >= 5.5),
    note: pickNote(ieltsEquiv >= 6.5, 'Meets most university requirements', ieltsEquiv >= 6.0, 'Meets minimum; some unis need 6.5', 'Need IELTS 6.0+ for UK student visa'),
  });
  uResult.checks.push({
    label: 'Academic Gap',
    status: hasGap ? 'caution' : 'pass',
    note: hasGap ? 'SOP must explain gap convincingly' : 'No significant gap',
  });
  uResult.score = uResult.checks.filter(c => c.status === 'pass').length;
  results.push(uResult);

  // ─── CANADA ───
  const cResult = { country: 'Canada', flag: 'CA', slug: 'canada', checks: [] };
  cResult.checks.push({
    label: 'SDS Eligibility',
    status: gradeBy(ieltsEquiv >= 6.0 && fundsStrong, ieltsEquiv >= 6.0 && fundsMid),
    note: pickNote(ieltsEquiv >= 6.0 && fundsStrong, 'Eligible for Student Direct Stream', ieltsEquiv >= 6.0, 'SDS needs GIC (~CAD 20,635)', 'SDS requires IELTS 6.0+ overall'),
  });
  cResult.checks.push({
    label: 'GIC + Tuition Funds',
    status: gradeBy(fundsStrong, fundsMid),
    note: fundsStrong ? 'Sufficient for GIC + first year' : 'May need education loan for GIC deposit',
  });
  cResult.checks.push({
    label: 'English Proficiency',
    status: gradeBy(ieltsEquiv >= 6.0, ieltsEquiv >= 5.5),
    note: pickNote(ieltsEquiv >= 6.5, 'Excellent for top DLIs', ieltsEquiv >= 6.0, 'Meets SDS minimum', 'Most DLIs require IELTS 6.0+'),
  });
  cResult.checks.push({
    label: 'Academic Profile',
    status: gradeBy(academicStrong, academicMid),
    note: pickNote(academicStrong, 'Competitive for top Canadian universities', academicMid, 'Good for mid-tier DLIs', 'May limit university options'),
  });
  cResult.score = cResult.checks.filter(c => c.status === 'pass').length;
  results.push(cResult);

  // ─── AUSTRALIA ───
  const aResult = { country: 'Australia', flag: 'AU', slug: 'australia', checks: [] };
  aResult.checks.push({
    label: 'Genuine Temporary Entrant (GTE)',
    status: isPostgrad ? 'pass' : 'caution',
    note: isPostgrad ? 'Postgrad study strengthens GTE case' : 'GTE statement requires clear study intent',
  });
  aResult.checks.push({
    label: 'Financial Capacity (~AUD 24,505/yr)',
    status: gradeBy(fundsStrong, fundsMid),
    note: pickNote(fundsStrong, 'Meets financial capacity requirement', fundsMid, 'May need loan; proof of 12 months required', 'Australia has high financial requirements'),
  });
  aResult.checks.push({
    label: 'English Proficiency',
    status: gradeBy(ieltsEquiv >= 6.0, ieltsEquiv >= 5.5),
    note: pickNote(ieltsEquiv >= 6.5, 'Meets most Go8 university requirements', ieltsEquiv >= 6.0, 'Sufficient for many universities', 'Most programs need IELTS 6.0+'),
  });
  aResult.checks.push({ label: 'OSHC Health Cover', status: 'pass', note: 'OSHC can be arranged through university' });
  aResult.score = aResult.checks.filter(c => c.status === 'pass').length;
  results.push(aResult);

  // ─── IRELAND ───
  const iResult = { country: 'Ireland', flag: 'IE', slug: 'ireland', checks: [] };
  iResult.checks.push({
    label: 'Student Visa (Stamp 2)',
    status: gradeBy(isPostgrad && academicStrong, isPostgrad),
    note: (isPostgrad && academicStrong) ? 'Strong profile for Stamp 2 visa' : 'Irish immigration reviews full academic profile',
  });
  iResult.checks.push({
    label: 'Proof of Funds (\u20AC10,000+)',
    status: (fundsStrong || fundsMid) ? 'pass' : 'fail',
    note: (fundsStrong || fundsMid) ? 'Meets Irish fund requirement' : 'Ireland requires \u20AC10,000 in bank',
  });
  iResult.checks.push({
    label: 'English Proficiency',
    status: gradeBy(ieltsEquiv >= 6.0, ieltsEquiv >= 5.5),
    note: pickNote(ieltsEquiv >= 6.5, 'Exceeds most Irish university requirements', ieltsEquiv >= 6.0, 'Meets minimum for most programs', 'Most Irish universities need IELTS 6.0+'),
  });
  iResult.checks.push({ label: 'IRP Registration (\u20AC300)', status: 'pass', note: 'IRP registration is done after arrival' });
  iResult.score = iResult.checks.filter(c => c.status === 'pass').length;
  results.push(iResult);

  // Sort by score
  results.sort((a, b) => b.score - a.score);

  // Assign overall status
  results.forEach(r => {
    const failCount = r.checks.filter(c => c.status === 'fail').length;
    const passCount = r.checks.filter(c => c.status === 'pass').length;
    if (failCount >= 2) r.overall = 'action_required';
    else if (passCount >= 3) r.overall = 'high';
    else r.overall = 'medium';
  });

  return results;
}

const StatusIcon = ({ status }) => {
  if (status === 'pass') return <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />;
  if (status === 'caution') return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
  return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
};

const OverallBadge = ({ overall }) => {
  const map = {
    high: { bg: 'bg-green-100 text-green-800', label: 'High Eligibility' },
    medium: { bg: 'bg-amber-100 text-amber-800', label: 'Medium Eligibility' },
    action_required: { bg: 'bg-red-100 text-red-800', label: 'Action Required' },
  };
  const { bg, label } = map[overall] || map.medium;
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${bg}`}>{label}</span>;
};

const VisaEligibilityPage = () => {
  usePageMeta(
    'Student Visa Eligibility Checker | Free 2-Minute Assessment',
    'Check your student-visa eligibility for Germany, UK, Canada, Australia, Ireland in 2 minutes. Free assessment covering academics, English scores, funds & documents — by MEA-approved counsellors.',
  );
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    qualification: '', passingYear: '', gpa: '',
    englishTest: '', englishScore: '', funds: '', destination: '',
  });
  const [showResults, setShowResults] = useState(false);

  // ─── PDF + Email Lead Capture Modal ───
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfForm, setPdfForm] = useState({ name: '', email: '' });
  const lqe = useLeadQualityEngine();
  const [pdfSubmitting, setPdfSubmitting] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);

  const update = (key, val) => { setData(prev => ({ ...prev, [key]: val })); setShowResults(false); };
  const next = () => setStep(s => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return data.qualification && data.passingYear;
    if (step === 1) return data.gpa;
    if (step === 2) return data.englishTest && (data.englishTest === 'none' || data.englishScore);
    if (step === 3) return data.funds;
    if (step === 4) return data.destination;
    return false;
  };

  const handleFinish = () => { if (canProceed()) setShowResults(true); };
  const results = showResults ? evaluateEligibility(data) : [];
  const selectedCountry = showResults ? results.find(r => r.slug === data.destination) : null;

  // ─── Generate PDF Report (delegated to utils/eligibilityPdf.js) ───
  const generatePDF = (userName) =>
    buildEligibilityPdf({
      userName,
      data,
      results,
      dictionaries: { qualificationOptions, gpaOptions, fundsOptions, countryOptions },
    });

  const handlePdfSubmit = async (e) => {
    e.preventDefault();
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    setPdfSubmitting(true);
    try {
      // 1. Generate PDF
      const doc = generatePDF(pdfForm.name);
      const filename = `ZAFCO-Visa-Eligibility-Report-${(pdfForm.name || 'Student').replace(/\s+/g, '-')}.pdf`;

      // 2. Trigger client-side download
      doc.save(filename);

      const countryLabel = countryOptions.find(c => c.value === data.destination)?.label || '';

      // 3. Async lead dispatch to backend → Zoho CRM
      fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: pdfForm.name || 'PDF Download Lead',
          email: pdfForm.email,
          phone: lqe.phone,
          ...guard.payloadMeta,
          service_type: 'Study Abroad - Visa Eligibility PDF',
          country: countryLabel || 'Not specified',
          qualification: qualificationOptions.find(q => q.value === data.qualification)?.label || '',
          experience: data.englishTest === 'none' ? 'Not Taken' : `${data.englishTest.toUpperCase()} ${data.englishScore}`,
          source: 'visa-eligibility-pdf-download',
          message: `[Visa Eligibility PDF Download — visa-eligibility-pdf-download] Country: ${countryLabel}, Eligibility: ${(selectedCountry?.overall || 'medium').toUpperCase()}, GPA: ${gpaOptions.find(g => g.value === data.gpa)?.label || 'N/A'}, English: ${data.englishTest === 'none' ? 'Not Taken' : `${data.englishTest.toUpperCase()} ${data.englishScore}`}, Funds: ${fundsOptions.find(f => f.value === data.funds)?.label || 'N/A'}.`,
        }),
      }).catch(() => { /* async, ignore */ });
      lqe.markSubmitSuccess();

      // 4. Async transactional email via Resend (soft-skip if not configured)
      try {
        const pdfBase64 = doc.output('datauristring').split(',')[1];
        fetch(`${API_URL}/api/study-abroad/send-eligibility-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: pdfForm.name,
            email: pdfForm.email,
            phone: lqe.phone,
            website: lqe.honeypot,
            pdf_base64: pdfBase64,
            country: countryLabel,
            eligibility_summary: selectedCountry?.overall || 'medium',
          }),
        }).catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('eligibility-report email dispatch failed:', err);
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('PDF base64 fallback failed:', err);
        }
      }

      setPdfDone(true);
    } catch (err) {
      setPdfDone(true);
    } finally {
      setPdfSubmitting(false);
    }
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setTimeout(() => { setPdfDone(false); setPdfForm({ name: '', email: '' }); }, 300);
  };

  return (
    <div data-testid="sa-visa-eligibility">
      {/* Hero */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-80 h-80 bg-[#C5A028] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Shield className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Interactive Assessment</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Visa <span className="text-[#C5A028]">Eligibility Checker</span>
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            Check your student visa eligibility for Germany, UK, Canada, Australia &amp; Ireland
            in under 60 seconds.
          </p>
        </div>
      </section>

      {!showResults ? (
        /* ─── Wizard Form ─── */
        <section className="py-12 lg:py-16 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            {/* Progress */}
            <div className="flex items-center justify-between mb-8">
              {steps.map((s, i) => {
                // Stepper visual state (replaces nested ternary)
                let stepClass;
                if (i < step) stepClass = 'bg-[#003087] text-white cursor-pointer';
                else if (i === step) stepClass = 'bg-[#C5A028] text-black';
                else stepClass = 'bg-slate-200 text-slate-500';
                return (
                <div key={s.id} className="flex items-center">
                  <button
                    onClick={() => i < step && setStep(i)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${stepClass}`}
                    data-testid={`step-indicator-${i}`}
                  >
                    {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </button>
                  {i < steps.length - 1 && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-1 ${i < step ? 'bg-[#003087]' : 'bg-slate-200'}`} />
                  )}
                </div>
                );
              })}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 shadow-sm" data-testid="sa-visa-form">
              <div className="flex items-center gap-3 mb-6">
                {(() => { const Icon = steps[step].icon; return <Icon className="w-5 h-5 text-[#003087]" />; })()}
                <h2 className="text-lg font-bold font-syne text-[#003087]">{steps[step].title}</h2>
                <span className="text-xs text-slate-400 ml-auto">Step {step + 1} of {steps.length}</span>
              </div>

              {/* Step 0: Qualification */}
              {step === 0 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">Highest Qualification</label>
                    <div className="grid grid-cols-2 gap-3">
                      {qualificationOptions.map(o => (
                        <button key={o.value} type="button" onClick={() => update('qualification', o.value)}
                          className={`p-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${data.qualification === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`qual-${o.value}`}
                        >{o.label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">Year of Completion</label>
                    <div className="grid grid-cols-5 gap-2">
                      {passingYearOptions.map(o => (
                        <button key={o.value} type="button" onClick={() => update('passingYear', o.value)}
                          className={`p-2.5 rounded-xl border-2 text-sm font-medium text-center transition-all ${data.passingYear === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`year-${o.value}`}
                        >{o.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1: Academics */}
              {step === 1 && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Academic Percentage / GPA</label>
                  <div className="space-y-3">
                    {gpaOptions.map(o => (
                      <button key={o.value} type="button" onClick={() => update('gpa', o.value)}
                        className={`w-full p-3.5 rounded-xl border-2 text-left transition-all flex justify-between items-center ${data.gpa === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`gpa-${o.value}`}
                      >
                        <span className="text-sm font-medium text-slate-900">{o.label}</span>
                        <span className="text-xs text-slate-500">{o.gpa} GPA</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: English */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-3">English Test Taken</label>
                    <div className="grid grid-cols-2 gap-3">
                      {englishTestOptions.map(o => (
                        <button key={o.value} type="button" onClick={() => { update('englishTest', o.value); setData(prev => ({ ...prev, englishScore: '' })); }}
                          className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all ${data.englishTest === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`eng-${o.value}`}
                        >{o.label}</button>
                      ))}
                    </div>
                  </div>
                  {data.englishTest && data.englishTest !== 'none' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-3">Your Score</label>
                      <div className="space-y-2">
                        {(englishScoreOptions[data.englishTest] || []).map(o => (
                          <button key={o.value} type="button" onClick={() => update('englishScore', o.value)}
                            className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${data.englishScore === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`score-${o.value}`}
                          >{o.label}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Funds */}
              {step === 3 && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Available Liquid Funds (INR)</label>
                  <div className="space-y-3">
                    {fundsOptions.map(o => (
                      <button key={o.value} type="button" onClick={() => update('funds', o.value)}
                        className={`w-full p-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all ${data.funds === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`funds-${o.value}`}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Destination */}
              {step === 4 && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Preferred Destination</label>
                  <div className="space-y-3">
                    {countryOptions.map(o => (
                      <button key={o.value} type="button" onClick={() => update('destination', o.value)}
                        className={`w-full p-3.5 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${data.destination === o.value ? 'border-[#003087] bg-[#003087]/5' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`dest-${o.value}`}
                      >
                        <span className="text-xl">{getFlagEmoji(o.flag)}</span>
                        <span className="text-sm font-medium text-slate-900">{o.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
                {step > 0 ? (
                  <button onClick={prev} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#003087] transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                ) : <div />}
                {step < steps.length - 1 ? (
                  <button onClick={next} disabled={!canProceed()}
                    className="flex items-center gap-2 bg-[#003087] hover:bg-[#001F5C] text-white font-bold text-sm px-6 py-2.5 rounded-full transition-all disabled:opacity-40"
                    data-testid="visa-next-btn"
                  >Next <ArrowRight className="w-4 h-4" /></button>
                ) : (
                  <button onClick={handleFinish} disabled={!canProceed()}
                    className="flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-6 py-2.5 rounded-full transition-all disabled:opacity-40 sa-pulse-cta"
                    data-testid="visa-check-btn"
                  >Check Eligibility <ArrowRight className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : (
        /* ─── Results Scorecard ─── */
        <section className="py-12 lg:py-20 bg-white" data-testid="sa-visa-results">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="sa-gold-line mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] mb-2">
                Your Eligibility Scorecard
              </h2>
              <p className="text-sm text-slate-500">
                Based on your profile: {qualificationOptions.find(q => q.value === data.qualification)?.label},
                {' '}{gpaOptions.find(g => g.value === data.gpa)?.label},
                {' '}{data.englishTest === 'none' ? 'No English Test' : `${data.englishTest.toUpperCase()} ${data.englishScore}`}
              </p>
            </div>

            {/* Preferred Country Highlight */}
            {selectedCountry && (() => {
              // Lookup map replaces nested ternary for tier → wrapper class
              const tierClassMap = {
                high: 'bg-green-50 border-green-200',
                medium: 'bg-amber-50 border-amber-200',
              };
              const tierClass = tierClassMap[selectedCountry.overall] || 'bg-red-50 border-red-200';
              return (
              <div className={`rounded-2xl p-6 mb-8 border-2 ${tierClass}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getFlagEmoji(selectedCountry.flag)}</span>
                    <div>
                      <h3 className="text-lg font-bold font-syne text-slate-900">{selectedCountry.country}</h3>
                      <span className="text-xs text-slate-500">Your preferred destination</span>
                    </div>
                  </div>
                  <OverallBadge overall={selectedCountry.overall} />
                </div>
                <div className="space-y-2.5">
                  {selectedCountry.checks.map((c) => (
                    <div key={`sel-${c.label}`} className="flex items-start gap-3 bg-white rounded-lg p-3">
                      <StatusIcon status={c.status} />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{c.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{c.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })()}

            {/* All Countries Grid */}
            <h3 className="text-base font-bold font-syne text-[#003087] mb-4">All Countries Comparison</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              {results.filter(r => r.slug !== data.destination).map(r => (
                <div key={r.slug} className="border border-slate-200 rounded-xl p-5" data-testid={`visa-result-${r.slug}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getFlagEmoji(r.flag)}</span>
                      <h4 className="text-sm font-bold text-slate-900">{r.country}</h4>
                    </div>
                    <OverallBadge overall={r.overall} />
                  </div>
                  <div className="space-y-2">
                    {r.checks.map((c) => (
                      <div key={`${r.slug}-${c.label}`} className="flex items-center gap-2">
                        <StatusIcon status={c.status} />
                        <span className="text-xs text-slate-700">{c.label}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/study-abroad/${r.slug}/`} className="flex items-center gap-1 mt-3 text-xs font-semibold text-[#003087] hover:text-[#C5A028] transition-colors">
                    Explore {r.country} <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#003087] to-[#001F5C] rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold font-syne text-white mb-2">
                Get a Detailed Visa Assessment
              </h3>
              <p className="text-sm text-slate-300 mb-6 max-w-lg mx-auto">
                Our certified immigration counsellors will review your complete profile and
                provide a step-by-step visa roadmap with document checklists.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to={`/study-abroad/book-counselling/?country=${encodeURIComponent(countryOptions.find(c => c.value === data.destination)?.label || '')}&eligibility=${selectedCountry?.overall || ''}&qualification=${data.qualification}&english=${data.englishTest}-${data.englishScore}`}
                  className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all sa-pulse-cta"
                  data-testid="sa-visa-cta"
                >
                  Book Free Visa Consultation <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => setShowPdfModal(true)}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-all"
                  data-testid="sa-visa-pdf-btn"
                >
                  <Download className="w-4 h-4" />
                  Download Official Eligibility Report (PDF) & Email to Parents
                </button>
              </div>
            </div>

            {/* Retry */}
            <div className="text-center mt-6">
              <button onClick={() => { setShowResults(false); setStep(0); }}
                className="text-sm text-slate-500 hover:text-[#003087] transition-colors"
              >Retake Assessment</button>
            </div>
          </div>
        </section>
      )}

      {/* ─── PDF Download Modal ─── */}
      {showPdfModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-in fade-in"
          onClick={closePdfModal}
          data-testid="sa-pdf-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePdfModal}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
              data-testid="sa-pdf-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {!pdfDone ? (
              <form onSubmit={handlePdfSubmit} className="p-7" data-testid="sa-pdf-form">
                <div className="w-12 h-12 rounded-full bg-[#003087]/10 flex items-center justify-center mb-4">
                  <Download className="w-5 h-5 text-[#003087]" />
                </div>
                <h3 className="text-lg font-bold font-syne text-[#003087] mb-1">
                  Get Your Official Report
                </h3>
                <p className="text-sm text-slate-500 mb-5">
                  We'll send a formatted PDF of your eligibility assessment that you can share
                  with your parents and review offline.
                </p>

                <div className="space-y-3">
                  <input
                    type="text" required placeholder="Your Full Name"
                    className="sa-input" value={pdfForm.name}
                    onChange={(e) => setPdfForm({ ...pdfForm, name: e.target.value })}
                    data-testid="sa-pdf-name"
                  />
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                    <input
                      type="email" required placeholder="Email Address"
                      className="sa-input" style={{ paddingLeft: '38px' }} value={pdfForm.email}
                      onChange={(e) => setPdfForm({ ...pdfForm, email: e.target.value })}
                      data-testid="sa-pdf-email"
                    />
                  </div>
                  <LeadPhoneField engine={lqe} testId="sa-pdf-phone" />
                </div>

                <PrivacyConsentNotice tone="light" testid="sa-pdf-consent" className="mt-4" />
                <button
                  type="submit" disabled={pdfSubmitting}
                  className="mt-5 w-full flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full transition-all disabled:opacity-60"
                  data-testid="sa-pdf-submit"
                >
                  {pdfSubmitting ? 'Generating Report…' : (<>Download PDF & Send to My Email <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-3">
                  By submitting, you agree to be contacted by a ZAFCO counsellor.
                </p>
              </form>
            ) : (
              <div className="p-8 text-center" data-testid="sa-pdf-success">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-lg font-bold font-syne text-[#003087] mb-1">
                  Report Downloaded!
                </h3>
                <p className="text-sm text-slate-500 mb-5">
                  Your eligibility report has been downloaded. A copy has also been queued for
                  delivery to <span className="font-semibold text-slate-700">{pdfForm.email}</span>.
                </p>
                <button
                  onClick={closePdfModal}
                  className="text-sm font-semibold text-[#003087] hover:text-[#C5A028] transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisaEligibilityPage;
